import {
  APICurrentUser,
  APICurrentUserPermission,
  ArdoqId,
  PersonalSetting,
  AssetType,
  EmailPreferenceConsent,
  UserWithRoles,
  BulkUpdatedUsersRolePayload,
  BulkUpdatedUsersPrivilegePayload,
  BulkUpdatedUsersCombinedPayload,
  BulkRemoveUsersPayload,
  ChangeEmailRequest,
  ChangePasswordRequest,
  ResetPasswordResponse,
} from '@ardoq/api-types';
import { ApiResponse } from './types';
import {
  headers,
  toRequestOptions,
  Method,
  fetchJson,
  fetchVoid,
  crudApi,
} from './api';

const userCrudApi = crudApi<APICurrentUser>('user');

const fetchCurrentUser = (): ApiResponse<APICurrentUser> => {
  const request = new Request('/api/user/current_user', { headers });
  return fetchJson<APICurrentUser>(request);
};

/**
 * Overwrites the whole user settings object!
 */
const updateSettings = ({
  userId,
  settings,
}: {
  userId: ArdoqId;
  settings: Partial<Record<PersonalSetting, any>>;
}): ApiResponse<APICurrentUser> => {
  const request = new Request(
    `/api/user/${userId}/settings`,
    toRequestOptions(Method.PUT, settings)
  );
  return fetchJson<APICurrentUser>(request);
};

export type FavoritePayload = {
  _id: ArdoqId;
  type: AssetType;
};

export enum FavoriteAction {
  ADD = 'add',
  REMOVE = 'remove',
}
const updateFavorite = ({
  action,
  userId,
  payload,
}: {
  action: FavoriteAction;
  userId: ArdoqId;
  payload: FavoritePayload;
}): ApiResponse<APICurrentUser> => {
  const request = new Request(
    `/api/user/${userId}/favorite/${action}`,
    toRequestOptions(Method.PUT, payload)
  );
  return fetchJson<APICurrentUser>(request);
};

const changePassword = async (
  userId: ArdoqId,
  values: ChangePasswordRequest
): ApiResponse<void> => {
  const request = new Request(
    `/api/user/${userId}/password`,
    toRequestOptions(Method.PUT, values)
  );
  return fetchVoid(request);
};

const triggerPasswordReset = (
  email: string
): ApiResponse<ResetPasswordResponse> => {
  const request = new Request(
    `/api/user/reset_password/trigger`,
    toRequestOptions(Method.POST, { email })
  );
  return fetchJson<ResetPasswordResponse>(request);
};

const changeEmail = async (
  userId: ArdoqId,
  values: ChangeEmailRequest
): ApiResponse<void> => {
  const request = new Request(
    `/api/user/${userId}/email`,
    toRequestOptions(Method.PUT, values)
  );
  return fetchVoid(request);
};

const fetchCurrentUserPermissions = (): ApiResponse<
  APICurrentUserPermission[]
> => {
  const request = new Request('/api/user/current_user/permissions', {
    headers,
  });
  return fetchJson<APICurrentUserPermission[]>(request);
};

const getConsent = (userId: ArdoqId): ApiResponse<EmailPreferenceConsent> => {
  const request = new Request(`/api/user/${userId}/consent`, { headers });
  return fetchJson<EmailPreferenceConsent>(request);
};

const updateConsent = (
  userId: ArdoqId,
  consent: EmailPreferenceConsent
): ApiResponse<void> => {
  const request = new Request(
    `/api/user/${userId}/consent`,
    toRequestOptions(Method.PUT, consent)
  );

  return fetchVoid(request);
};

const acceptTerms = (userId: ArdoqId): ApiResponse<void> => {
  const request = new Request(
    `/api/user/${userId}/accept-terms`,
    toRequestOptions(Method.PUT, undefined)
  );

  return fetchVoid(request);
};

const search = (
  searchQuery: string,
  orgMembersOnly: boolean,
  limit: number = 100
): ApiResponse<UserWithRoles[]> => {
  const searchParams = new URLSearchParams({
    q: searchQuery,
    orgMembersOnly: orgMembersOnly.toString(),
    limit: limit.toString(),
  });

  const request = new Request(`/api/user/search?${searchParams}`, {
    headers,
  });
  return fetchJson<UserWithRoles[]>(request);
};

type BulkUpdate =
  | BulkUpdatedUsersRolePayload
  | BulkUpdatedUsersPrivilegePayload
  | BulkUpdatedUsersCombinedPayload
  | BulkRemoveUsersPayload;

const bulkUpdate = (bulkUpdatePayload: BulkUpdate): ApiResponse<BulkUpdate> => {
  const request = new Request(
    '/api/user/bulk-operation/submit',
    toRequestOptions(Method.POST, bulkUpdatePayload)
  );
  return fetchJson<BulkUpdate>(request);
};

export const userApi = {
  acceptTerms,
  fetchCurrentUser,
  fetchCurrentUserPermissions,
  updateSettings,
  changePassword,
  triggerPasswordReset,
  changeEmail,
  updateFavorite,
  getConsent,
  updateConsent,
  search,
  bulkUpdate,
  update: userCrudApi.update,
};
